// View-only grouping of books / chat sessions by their knowledge base.
// Read models already carry KB references (by NAME, not id), so grouping is a
// pure client-side transform — no backend, schema, or API change.
//
// Rules (see docs/02_설계/기능원자_명세서.md §7):
//  - a multi-KB item is exposed in EACH of its KB groups (duplicate exposure);
//  - an item with no KB falls into the "Uncategorized" bucket, always last;
//  - named groups are ordered by KB name; a deleted KB still groups by identity.

export const UNCATEGORIZED_KEY = "__uncategorized__";

export interface KnowledgeGroup<T> {
  /** KB name, or UNCATEGORIZED_KEY for the no-KB bucket. Stable React key. */
  key: string;
  /** KB name for display; empty for the bucket (caller supplies i18n label). */
  label: string;
  isUncategorized: boolean;
  items: T[];
}

export function groupByKnowledge<T>(
  items: T[],
  getKnowledgeBases: (item: T) => readonly string[] | null | undefined,
): KnowledgeGroup<T>[] {
  const named = new Map<string, T[]>();
  const uncategorized: T[] = [];

  for (const item of items) {
    const names = Array.from(
      new Set(
        (getKnowledgeBases(item) ?? [])
          .map((n) => (n ?? "").trim())
          .filter((n) => n.length > 0),
      ),
    );
    if (names.length === 0) {
      uncategorized.push(item);
      continue;
    }
    for (const name of names) {
      const bucket = named.get(name);
      if (bucket) bucket.push(item);
      else named.set(name, [item]);
    }
  }

  const groups: KnowledgeGroup<T>[] = Array.from(named.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([name, groupItems]) => ({
      key: name,
      label: name,
      isUncategorized: false,
      items: groupItems,
    }));

  if (uncategorized.length > 0) {
    groups.push({
      key: UNCATEGORIZED_KEY,
      label: "",
      isUncategorized: true,
      items: uncategorized,
    });
  }

  return groups;
}
